'use server';

import bcrypt from 'bcrypt';

import { logAuditEvent } from '@/lib/audit-log';
import { getSession } from '@/lib/auth-session';
import { prisma } from '@/lib/db';
import { sendEmail } from '@/lib/email';
import { type ActionResult, failure, ok } from '@/lib/types/action-result';

import {
    createPasswordResetToken,
    getMissingSmtpConfig,
    getRequestIp,
    isSmtpConfigured,
    MIN_PASSWORD_LENGTH,
    passwordResetIpLimiter,
    passwordResetLimiter,
    pwProbeLimiter,
    resetTokenIpLimiter,
    resetTokenLimiter,
    safeLogSystemError,
    SALT_ROUNDS,
} from '../users-shared';

export async function changePassword(oldPw: string, newPw: string): Promise<ActionResult> {
    try {
        const session = await getSession();
        if (!session?.user?.email) return failure('Nicht eingeloggt');

        if (!newPw || newPw.length < MIN_PASSWORD_LENGTH) {
            return failure(`Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
        }

        const user = await prisma.user.findUnique({ where: { email: session.user.email } });
        if (!user) return failure('User nicht gefunden');

        if (user.password) {
            if (!oldPw) {
                return failure('Bitte geben Sie das aktuelle Passwort ein.');
            }
            const match = await bcrypt.compare(oldPw, user.password);
            if (!match) return failure('Das aktuelle Passwort ist falsch.');
        }

        const hashed = await bcrypt.hash(newPw, SALT_ROUNDS);
        await prisma.user.update({
            where: { id: user.id },
            data: { password: hashed }
        });

        // Ensure credential account exists for Better Auth login
        const credentialAccount = await prisma.account.findFirst({
            where: { userId: user.id, providerId: 'credential' },
        });
        if (credentialAccount) {
            await prisma.account.update({
                where: { id: credentialAccount.id },
                data: { password: hashed },
            });
        } else {
            await prisma.account.create({
                data: {
                    userId: user.id,
                    accountId: String(user.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(user.id),
                    type: 'credential',
                    password: hashed,
                },
            });
        }

        await logAuditEvent({
            userId: user.id,
            action: 'user.password_change',
            resourceType: 'User',
            resourceId: user.id,
            metadata: { method: 'self_service' },
        });

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User password change failed', e);
        return failure('Fehler beim Ändern des Passworts');
    }
}

export async function requestPasswordReset(email: string): Promise<ActionResult> {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) return failure('Email ist erforderlich');

        const ip = await getRequestIp();
        // Two-key limiter: per-email (5/5min) catches a focused attack on
        // one account, per-IP (20/5min) catches enumeration across many
        // emails which would otherwise bypass the per-email key.
        const [{ success: emailOk }, { success: ipOk }] = await Promise.all([
            passwordResetLimiter.limit(`pw-reset:${normalizedEmail}`),
            passwordResetIpLimiter.limit(`pw-reset-ip:${ip}`),
        ]);
        if (!emailOk || !ipOk) {
            return failure('Zu viele Anfragen. Bitte versuchen Sie es später erneut.');
        }

        if (!isSmtpConfigured()) {
            const missing = getMissingSmtpConfig();
            const missingHint = missing.length > 0 ? ` (fehlt: ${missing.join(', ')})` : '';
            return failure(`SMTP ist nicht konfiguriert${missingHint}`);
        }

        const user = await prisma.user.findUnique({ where: { email: normalizedEmail } });

        if (user?.email) {
            const token = await createPasswordResetToken(user.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const resetUrl = `${baseUrl.replace(/\/$/, '')}/reset-password?token=${token}`;
            const emailResult = await sendEmail({
                to: user.email,
                subject: 'Passwort zurücksetzen',
                text: `Sie können Ihr Passwort zurücksetzen: ${resetUrl}`,
                html: `<p>Sie können Ihr Passwort zurücksetzen:</p><p><a href=\"${resetUrl}\">${resetUrl}</a></p>`,
            });
            // Code-review S-A (2026-05-13): swallow SMTP failure so the
            // existence of the e-mail isn't observable through the
            // distinction "ok vs SMTP-error returns failure". Log
            // server-side so operators see the SMTP issue.
            if (emailResult.error) {
                void safeLogSystemError('Password-reset e-mail SMTP failure', undefined, {
                    smtpError: emailResult.error,
                    // intentional: no email here — redacted by S-E anyway
                });
            }
        }

        // ALWAYS return ok() — the caller cannot distinguish "e-mail
        // unknown" from "e-mail known but SMTP down".
        return ok();
    } catch (e) {
        void safeLogSystemError('Password reset request failed', e);
        // Same generic response on programming errors — leaking
        // implementation hiccups distinguishes account states.
        return ok();
    }
}

/**
 * Typed sentinel used to abort the password-reset $transaction. We must
 * `throw` to roll the transaction back, but the OUTER catch keys off the
 * `code` property (not message string parsing) and converts to a typed
 * `ActionResult` failure with the same code.
 */
class ResetTokenError extends Error {
    constructor(public readonly code: 'TOKEN_INVALID' | 'TOKEN_EXPIRED') {
        super(code);
        this.name = 'ResetTokenError';
    }
}

export async function resetPasswordWithToken(
    token: string,
    newPassword: string,
): Promise<ActionResult> {
    try {
        if (!token || !newPassword) return failure('Ungültige Anfrage', 'INVALID_REQUEST');

        // Code-review L-F (2026-05-13): the token-prefix-only limiter
        // could be defeated by submitting distinct fake tokens — each
        // got its own 5/5-min bucket. Pair with an IP-keyed limiter so
        // distinct tokens from one IP share a budget.
        const ip = await getRequestIp();
        const [{ success: tokenOk }, { success: ipOk }] = await Promise.all([
            resetTokenLimiter.limit(`pw-reset-token:${token.slice(0, 16)}`),
            resetTokenIpLimiter.limit(`pw-reset-token-ip:${ip}`),
        ]);
        if (!tokenOk || !ipOk) return failure('Zu viele Versuche. Bitte später erneut versuchen.', 'RATE_LIMITED');

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            return failure(
                `Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`,
                'PASSWORD_TOO_SHORT',
            );
        }

        const hashedPassword = await bcrypt.hash(newPassword, SALT_ROUNDS);

        // Hoist out of the transaction so we can audit-log AFTER commit
        // using the userId we resolved from the consumed token.
        let resetUserId: number | null = null;

        await prisma.$transaction(async (tx) => {
            const resetToken = await tx.passwordResetToken.findUnique({
                where: { token },
            });

            // Throws inside $transaction are how we abort the transaction —
            // we use a typed error class so the outer catch can branch on
            // `instanceof` / `code` instead of parsing `e.message`.
            if (!resetToken) throw new ResetTokenError('TOKEN_INVALID');
            if (resetToken.expiresAt < new Date()) {
                await tx.passwordResetToken.delete({ where: { token } });
                throw new ResetTokenError('TOKEN_EXPIRED');
            }

            resetUserId = resetToken.userId;

            await tx.user.update({
                where: { id: resetToken.userId },
                data: { password: hashedPassword },
            });

            // Ensure credential account exists for Better Auth login
            const existing = await tx.account.findFirst({
                where: { userId: resetToken.userId, providerId: 'credential' },
            });
            if (existing) {
                await tx.account.update({
                    where: { id: existing.id },
                    data: { password: hashedPassword },
                });
            } else {
                await tx.account.create({
                    data: {
                        userId: resetToken.userId,
                        accountId: String(resetToken.userId),
                        providerId: 'credential',
                        provider: 'credential',
                        providerAccountId: String(resetToken.userId),
                        type: 'credential',
                        password: hashedPassword,
                    },
                });
            }

            await tx.passwordResetToken.deleteMany({ where: { userId: resetToken.userId } });
        });

        // Audit OUTSIDE the transaction so an audit-log failure cannot
        // roll back the password change.
        if (resetUserId !== null) {
            try {
                await logAuditEvent({
                    userId: resetUserId,
                    action: 'user.password_reset',
                    resourceType: 'User',
                    resourceId: resetUserId,
                    metadata: { method: 'reset_token' },
                });
            } catch (auditError) {
                void safeLogSystemError('Password reset audit log failed', auditError);
            }
        }

        return ok();
    } catch (e) {
        if (e instanceof ResetTokenError) {
            if (e.code === 'TOKEN_INVALID') {
                return failure('Token ist ungültig oder abgelaufen', 'TOKEN_INVALID');
            }
            return failure('Token ist abgelaufen', 'TOKEN_EXPIRED');
        }
        console.error(e);
        void safeLogSystemError('Password reset failed', e);
        return failure('Passwort konnte nicht zurückgesetzt werden', 'INTERNAL_ERROR');
    }
}

// Code-review L-A (2026-05-13): combined post-login probe.
// The old login flow called two separate server actions
// (getEmailVerificationStatus + checkUserSuspended) on every failed
// login — each ran bcrypt and consumed its own rate-limit tokens,
// burning 2-3 probes per failed attempt and tripping the limiter
// prematurely on shared NAT. This single-shot probe runs bcrypt
// once and reports verification + suspension via a discriminated
// union, so the form picks the right message in one round-trip.
export type LoginProbe =
    | { status: 'invalid' }
    | { status: 'verified' }
    | { status: 'suspended' }
    | { status: 'unverified'; tokenExpired: boolean; expiresAt?: string; pending?: boolean };

export async function getLoginProbe(email: string, password?: string): Promise<ActionResult<LoginProbe>> {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || !password) return ok({ status: 'invalid' });

        const ip = await getRequestIp();
        const [{ success: emailOk }, { success: ipOk }] = await Promise.all([
            pwProbeLimiter.limit(`pw-probe:${normalizedEmail}`),
            pwProbeLimiter.limit(`pw-probe-ip:${ip}`),
        ]);
        if (!emailOk || !ipOk) return ok({ status: 'invalid' });

        const user = await prisma.user.findFirst({
            where: {
                OR: [
                    { email: normalizedEmail },
                    { pendingEmail: normalizedEmail },
                ],
            },
            select: {
                id: true,
                emailVerified: true,
                pendingEmail: true,
                password: true,
                deletedAt: true,
                suspendedAt: true,
            },
        });
        if (!user || user.deletedAt || !user.password) return ok({ status: 'invalid' });

        const passwordMatches = await bcrypt.compare(password, user.password);
        if (!passwordMatches) return ok({ status: 'invalid' });

        // Suspended takes precedence over unverified — admins suspending
        // a user is more meaningful than an unfinished verification.
        if (user.suspendedAt) return ok({ status: 'suspended' });

        const pendingMatch = user.pendingEmail?.trim().toLowerCase() === normalizedEmail;
        if (user.emailVerified && !pendingMatch) return ok({ status: 'verified' });

        const token = await prisma.emailVerificationToken.findFirst({
            where: { userId: user.id },
            orderBy: { createdAt: 'desc' },
        });
        if (!token) return ok({ status: 'unverified', tokenExpired: true });

        const tokenExpired = token.expiresAt < new Date();
        return ok({
            status: 'unverified',
            tokenExpired,
            expiresAt: token.expiresAt.toISOString(),
            pending: pendingMatch,
        });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Login probe failed', e, { email });
        return failure('Status konnte nicht geladen werden');
    }
}
